import { createContext } from "react";
import { type MappingEntry, type WizardState } from "../types";
import { type CommitProgress } from "../hooks/useWorkflowCommit";
import { type ComponentResolutionMap } from "../utils/buildImportRows";
import type { MyPayEnvironment } from "../../generated/core-api";

/**
 * Inputs for a real commit run, handed from the Review screen to the provider.
 * `failedRowsCsv` is pre-computed for the "import valid only" path; when null the
 * provider derives it from the commit result's per-row errors.
 * `mappings` are the confirmed column→parameter mappings sent inline with the
 * commit invocation (the workflow uses no shared mapping store).
 */
export interface CommitImportArgs {
  csvText: string;
  providerId: string | null;
  componentColumn: string | null;
  /** Session override for the start-date CSV header (null → reserved `Start Date`). */
  startDateColumn: string | null;
  /** Session override for the end-date CSV header (null → reserved `End Date`). */
  endDateColumn: string | null;
  mypayEnvironment: MyPayEnvironment;
  failedRowsCsv: string | null;
  mappings: MappingEntry[];
  /**
   * Per-name ambiguity resolutions (D4), built on Review from discovery + the
   * user's picks. Threaded into the commit so ambiguous names carry their
   * `companyComponentId` and unresolved ones block before posting.
   */
  componentResolutions: ComponentResolutionMap;
}

/**
 * Value exposed by WizardContext to all screens within the wizard.
 */
export interface WizardContextValue {
  /**
   * The Christmas API company code used for all import/mapping endpoints.
   * Derived from the authenticated org context and passed in by `PayComponentImportLayout`.
   */
  companyCode: string;
  /** Display name from the company gate (redesign confirmations / headers). */
  companyName: string;
  /** Current wizard state. */
  state: WizardState;
  /**
   * Merge a partial update into the current wizard state.
   * Screens call this to persist their local changes (e.g. uploaded file,
   * provider id, pending mappings) into the shared context before navigating.
   */
  updateState: (partial: Partial<WizardState>) => void;
  /**
   * Mark the current dry-run result as stale without clearing it, so Review can
   * still show prior diagnostics while Confirm import stays disabled.
   */
  invalidateValidation: () => void;
  /**
   * Redesign: report local UI busy (CSV parse / mapping save) so the layout
   * can lock the company gate for the full validate pipeline.
   */
  reportUiBusy: (busy: boolean) => void;
  /**
   * Discard uploaded CSV, mappings, and validation (including sessionStorage)
   * while keeping the company gate environment and page-size preference.
   * Call before confirming "Leave anyway" so a return visit starts empty.
   */
  discardUnsavedProgress: () => void;
  /**
   * Run the import — POST the CSV file from `state.file` to the API.
   * Stores the result in `state.importResult` then navigates:
   *   - "needs_mapping" → /map (legacy) or base path (redesign)
   *   - dry-run (valid | invalid) → /review (validate before commit)
   *   - real commit "committed" → /results, "invalid" → /errors
   *
   * Pass `dryRun = true` to validate without committing (the review step).
   * Does nothing (no-op) when `state.file` is null.
   */
  runImport: (dryRun?: boolean) => Promise<void>;
  /**
   * Start the real (committing) import: navigates to the Results screen
   * immediately and runs the chunked Temporal-workflow commit in the
   * background, so the user watches live progress on Results instead of a
   * blocked button on Review.
   */
  commitImport: (args: CommitImportArgs) => Promise<void>;
  /** Per-chunk commit progress while a commit is in flight (null when idle). */
  commitProgress: CommitProgress | null;
  /** True while a commit workflow is running. */
  isCommitting: boolean;
  /** True while a dry-run / validate-only import request is in flight. */
  isValidating: boolean;
  /** Error message if the last commit failed (null otherwise). */
  commitError: string | null;
  /** Whether the redesign release flag is on for this session. */
  redesignEnabled: boolean;
}

/**
 * React context for the Pay Component Import wizard.
 * Use `useWizard()` (in `hooks/useWizard.ts`) to read it.
 * Provided by `WizardProvider` (in `WizardProvider.tsx`).
 */
export const WizardContext = createContext<WizardContextValue | undefined>(
  undefined,
);
